/**
 * How values read on screen: numbers, percentages, durations, times. The same rules for app code and views, so a win
 * rate looks the same wherever it's shown.
 */

export const FORMATS = ['number', 'integer', 'percent', 'compact', 'duration', 'time-ago', 'date', 'text'] as const
export type Format = (typeof FORMATS)[number]

/** What each format expects and shows, for the docs and the agent. */
export const FORMAT_DOCS: Record<Format, string> = {
  number: '1240.5 → "1,241"; 3.14159 → "3.14". The default for numbers.',
  integer: '1240.5 → "1,241".',
  percent: 'A fraction: 0.472 → "47.2%". If the number is already a percentage (47.2), use `unit: "%"` instead.',
  compact: '15300 → "15.3K"; 2100000 → "2.1M".',
  duration: 'Seconds: 200 → "3m 20s"; 5400 → "1h 30m".',
  'time-ago': 'An ISO time: "2026-10-14T09:00:00Z" → "2 hours ago".',
  date: 'An ISO time: "2026-10-14T09:00:00Z" → "Oct 14, 2026".',
  text: 'Shown as it is.',
}

/** A number for an axis or a tooltip: whole numbers as they are, others to a tenth (or two below 10). */
export function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toLocaleString('en')
  const abs = Math.abs(value)
  return abs >= 100 ? Math.round(value).toLocaleString('en') : value.toFixed(abs < 10 ? 2 : 1).replace(/\.?0+$/, '')
}

/** A value with its unit: "47%", "1,240 gold", "12 s". */
export function withUnit(value: number, unit: string): string {
  const n = formatNumber(value)
  return !unit ? n : unit === '%' ? `${n}%` : `${n} ${unit}`
}

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
]

/** "3 days ago", "in 20 minutes", "just now". */
export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return ''
  const seconds = (Date.parse(iso) - now) / 1000
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
  }
  return 'just now'
}

function duration(seconds: number): string {
  const s = Math.round(Math.abs(seconds))
  const sign = seconds < 0 ? '-' : ''
  if (s < 60) return `${sign}${s}s`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h) return `${sign}${h}h${m ? ` ${m}m` : ''}`
  const rest = s % 60
  return `${sign}${m}m${rest ? ` ${rest}s` : ''}`
}

const compactFormat = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
const dateFormat = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * A value as text, in `format` (else by its type), with `unit` after numbers. Null and missing read "–". Never throws:
 * a value the format can't read is shown as it is.
 */
export function formatValue(value: unknown, format?: Format | null, unit = ''): string {
  if (value === null || value === undefined || value === '' || typeof value === 'object') return '–'
  const f = format ?? (typeof value === 'number' ? 'number' : 'text')
  if (f === 'text' || typeof value === 'boolean') return unit ? `${String(value)} ${unit}` : String(value)
  if (f === 'time-ago' || f === 'date') {
    if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) return String(value)
    return f === 'date' ? dateFormat.format(new Date(value)) : timeAgo(value)
  }
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  switch (f) {
    case 'integer':
      return withUnit(Math.round(n), unit)
    case 'percent':
      return `${formatNumber(Number((n * 100).toFixed(1)))}%`
    case 'compact':
      return unit ? `${compactFormat.format(n)} ${unit}` : compactFormat.format(n)
    case 'duration':
      return duration(n)
    default:
      return withUnit(n, unit)
  }
}

/** A change, signed: "+3.1%", "−12", "±0". */
export function formatDelta(delta: number, format?: Format | null, unit = ''): string {
  if (delta === 0) return `±${formatValue(0, format, unit)}`
  const text = formatValue(Math.abs(delta), format, unit)
  return `${delta > 0 ? '+' : '−'}${text}`
}
